import { useEffect, useRef, useState } from 'react';
import { addressFromComponents, addressSummary, emptyAddress, type AddressFields } from './address';
import { loadGoogleMaps, MAPS_FAILURE_EVENT, useGoogleMapsSettings } from './googleMaps';
import type { FormErrors } from './requests';

const addressKeys = ['streetAddress', 'city', 'state', 'zipCode'] as const;
const labels = {
  streetAddress: 'Street address',
  city: 'City',
  state: 'State',
  zipCode: 'ZIP code',
};
const limits = { streetAddress: 200, city: 100, state: 50, zipCode: 10 };

/** Keep an existing unit editable after Google standardizes the street line. */
function splitUnit(streetAddress: string) {
  const match = streetAddress.match(
    /^(.+?)(?:,\s*|\s+)((?:(?:suite|ste|unit|apt|apartment|room|rm|floor|fl|building|bldg)\.?\s+|#\s*)\S.*)$/i,
  );
  return match && /[a-z]/i.test(match[1]!) && /\d/.test(match[1]!)
    ? { streetAddress: match[1]!.replace(/,\s*$/, ''), unit: match[2]! }
    : { streetAddress, unit: '' };
}

export function AddressEntry({
  value,
  onChange,
  userId,
  errors,
  disabled = false,
}: {
  value: AddressFields;
  onChange: (value: AddressFields) => void;
  userId: number | null;
  errors: FormErrors;
  disabled?: boolean;
}) {
  const maps = useGoogleMapsSettings(userId);
  const [manual, setManual] = useState(() => !!value.streetAddress && !value.googlePlaceId);
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed' | 'selecting'>('loading');
  const [message, setMessage] = useState<string | null>(null);
  const [unit, setUnit] = useState(() =>
    value.googlePlaceId ? splitUnit(value.streetAddress).unit : '',
  );
  const selectedBase = useRef<AddressFields | null>(
    value.googlePlaceId
      ? {
          streetAddress: splitUnit(value.streetAddress).streetAddress,
          city: value.city,
          state: value.state,
          zipCode: value.zipCode,
          googlePlaceId: value.googlePlaceId,
        }
      : null,
  );
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const host = useRef<HTMLDivElement>(null);
  const widget = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const generation = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (maps.state !== 'ready' || manual || !host.current) return;
    let cancelled = false;
    let element: google.maps.places.PlaceAutocompleteElement | undefined;
    setStatus('loading');
    const fail = () => {
      if (cancelled) return;
      ++generation.current;
      setStatus('failed');
      setMessage('Address search is unavailable. You can enter the address manually.');
    };
    window.addEventListener(MAPS_FAILURE_EVENT, fail);
    void loadGoogleMaps(maps.settings.browserKey)
      .then(async (mapsApi) => {
        const places = (await mapsApi.importLibrary('places')) as google.maps.PlacesLibrary;
        if (cancelled) return;
        element = new places.PlaceAutocompleteElement({
          includedRegionCodes: ['us'],
          includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
          locationBias: { north: 41.5, south: 40.3, east: -72.5, west: -74.5 },
          placeholder: 'Start typing a street address…',
          description: 'Search address. Choose a suggestion to fill in the complete address.',
        });
        element.id = 'new-stop-address-search';
        if (selectedBase.current) element.value = addressSummary(value);
        element.setAttribute('aria-label', 'Search address');
        element.setAttribute('aria-describedby', 'new-stop-address-help');
        element.setAttribute('autocomplete', 'off');
        element.addEventListener('input', () => {
          ++generation.current;
          selectedBase.current = null;
          setUnit('');
          setMessage(null);
          setStatus('ready');
          onChangeRef.current(emptyAddress());
        });
        element.addEventListener('gmp-select', (event) => {
          const current = ++generation.current;
          setStatus('selecting');
          setMessage(null);
          onChangeRef.current(emptyAddress());
          const place = (
            event as google.maps.places.PlacePredictionSelectEvent
          ).placePrediction.toPlace();
          void place
            .fetchFields({ fields: ['id', 'addressComponents', 'formattedAddress'] })
            .then(() => {
              if (cancelled || current !== generation.current) return;
              const address = addressFromComponents(place.addressComponents ?? [], place.id);
              const parts = splitUnit(address.streetAddress);
              selectedBase.current = { ...address, streetAddress: parts.streetAddress };
              setUnit(parts.unit);
              onChangeRef.current(address);
              setStatus('ready');
            })
            .catch((error: unknown) => {
              if (cancelled || current !== generation.current) return;
              setStatus('ready');
              setMessage(
                error instanceof Error && error.message.startsWith('This result')
                  ? error.message
                  : 'The selected address could not load. Choose another result or enter it manually.',
              );
            });
        });
        element.addEventListener('gmp-error', fail);
        host.current?.replaceChildren(element);
        widget.current = element;
        element.disabled = disabledRef.current;
        setStatus('ready');
      })
      .catch(fail);
    return () => {
      cancelled = true;
      ++generation.current;
      window.removeEventListener(MAPS_FAILURE_EVENT, fail);
      element?.remove();
      widget.current = null;
    };
    // Updating form values must not recreate a widget or its search session.
  }, [maps, manual]);

  useEffect(() => {
    if (widget.current) widget.current.disabled = disabled;
  }, [disabled]);

  const error = addressKeys.map((key) => errors[key]).find(Boolean);
  const unavailable = maps.state === 'unavailable' || status === 'failed';

  function changeMode(nextManual: boolean) {
    ++generation.current;
    setManual(nextManual);
    setMessage(null);
    setUnit('');
    selectedBase.current = null;
    onChangeRef.current(nextManual ? { ...value, googlePlaceId: undefined } : emptyAddress());
  }

  return (
    <div className="form-field address-entry">
      <div className="address-entry-heading">
        <label htmlFor={manual ? 'new-stop-streetAddress' : 'new-stop-address-search'}>
          Address<span className="required-mark"> *</span>
        </label>
        <button
          className="address-mode-button"
          type="button"
          disabled={disabled}
          onClick={() => changeMode(!manual)}
        >
          {manual ? 'Use address search' : 'Enter address manually'}
        </button>
      </div>
      {!manual && (
        <>
          <div ref={host} className={`address-search-host${unavailable ? ' is-unavailable' : ''}`}>
            <input
              id="new-stop-address-search"
              aria-label="Search address"
              placeholder="Start typing a street address…"
              disabled
            />
          </div>
          <p
            className={status === 'ready' && !unavailable ? 'visually-hidden' : 'address-help'}
            id="new-stop-address-help"
            role="status"
          >
            {unavailable
              ? 'Address search is unavailable. Select “Enter address manually” to continue.'
              : status === 'selecting'
                ? 'Getting the complete address…'
                : maps.state === 'loading' || status === 'loading'
                  ? 'Loading address search…'
                  : 'Choose a suggestion to fill in the complete address.'}
          </p>
          {value.googlePlaceId && (
            <div className="selected-address" role="status">
              <span className="selected-address-label">Selected address</span>
              <strong>{addressSummary(value)}</strong>
              <label htmlFor="new-stop-unit">Suite / unit (optional)</label>
              <input
                id="new-stop-unit"
                value={unit}
                maxLength={40}
                disabled={disabled}
                placeholder="Suite 2"
                onChange={(event) => {
                  const next = event.target.value;
                  setUnit(next);
                  if (selectedBase.current)
                    onChangeRef.current({
                      ...selectedBase.current,
                      streetAddress: [selectedBase.current.streetAddress, next.trim()]
                        .filter(Boolean)
                        .join(', '),
                    });
                }}
              />
            </div>
          )}
        </>
      )}
      {manual && (
        <div className="manual-address-fields">
          {addressKeys.map((key) => (
            <div
              className={`form-field${key === 'streetAddress' ? ' manual-street' : ''}`}
              key={key}
            >
              <label htmlFor={`new-stop-${key}`}>
                {labels[key]}
                <span className="required-mark"> *</span>
              </label>
              <input
                id={`new-stop-${key}`}
                value={value[key]}
                maxLength={limits[key]}
                disabled={disabled}
                aria-invalid={errors[key] ? true : undefined}
                aria-describedby={errors[key] ? `new-stop-${key}-error` : undefined}
                autoComplete={
                  {
                    streetAddress: 'street-address',
                    city: 'address-level2',
                    state: 'address-level1',
                    zipCode: 'postal-code',
                  }[key]
                }
                onChange={(event) =>
                  onChangeRef.current({
                    ...value,
                    [key]: event.target.value,
                    googlePlaceId: undefined,
                  })
                }
              />
              {errors[key] && (
                <p className="form-error" id={`new-stop-${key}-error`}>
                  {errors[key]}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {!manual && error && (
        <p className="form-error" role="alert">
          Choose a complete address from the suggestions or enter it manually.
        </p>
      )}
      {message && (
        <p className="form-error" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}
